import { useSoftphoneStore } from 'Modules/softphone/store';
import React, { useCallback } from 'react';
import { Icon } from '~/components/shared/Icon';
import { Input } from '~/lib/ui/input';
import { cn } from '~/lib/utils';
import { TransferSearchInputProps } from './index.types';

/**
 * TransferSearchInput component displays a search input field for transferring calls.
 */
export const TransferSearchInput = ({
  testID,
}: TransferSearchInputProps): React.ReactElement => {
  const componentTestId = testID ? `${testID}-input` : 'input'
  const { searchTerm, setSearchTerm } = useSoftphoneStore((state) => ({
    searchTerm: state.searchTerm,
    setSearchTerm: state.setSearchTerm,
  }));

  const handleBackspace = useCallback(() => {
    setSearchTerm(searchTerm.slice(0, -1));
  }, [searchTerm, setSearchTerm]);

  return (
    <div
      className="relative mx-[16px] flex items-center"
      data-testid={`${componentTestId}-wrapper`}
    >
      <Input
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search contact"
        className={cn('px-[16px] py-[10px]', searchTerm.length > 0 && 'pr-[40px]')}
        aria-label="Search contacts or enter number"
        data-testid={`${componentTestId}`}
        autoFocus
      />

      {searchTerm.length > 0 && (
        <button
          type="button"
          onClick={handleBackspace}
          className={cn(
            'absolute right-[12px]',
            'cursor-pointer',
            'flex-shrink-0 size-[24px] rounded-[4px]',
            'flex items-center justify-center',
            'text-muted-foreground',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-ring/50'
          )}
          data-testid={`${componentTestId}-backspace`}
          aria-label="Backspace"
        >
          <Icon icon="backspace" size="medium" />
        </button>
      )}
    </div>
  );
};

TransferSearchInput.displayName = 'TransferSearchInput';
